import { useState } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import axios, { API, authHeaders, formatError } from "../../lib/api";

const STATUS_OPTIONS = [
  ["running", "Running", "bg-emerald-400"],
  ["maintenance", "Under maintenance", "bg-red-400"],
  ["holiday", "Holiday", "bg-amber-400"],
  ["setup", "Setup", "bg-sky-400"],
  ["closed", "Closed", "bg-slate-400"],
];

const NUM = [
  ["today_output_t", "Today's output (tonnes)", "admin-production-input"],
  ["capacity_tpd", "Daily capacity (tonnes)", "admin-capacity-input"],
  ["presses_active", "Presses running", "admin-presses-active-input"],
  ["presses_total", "Presses total", "admin-presses-total-input"],
];

export const PlantForm = ({ plant, onSaved }) => {
  const [f, setF] = useState({ ...plant, status: ["running", "maintenance", "holiday", "setup", "closed"].includes(plant?.status) ? plant.status : "running", notice: plant?.notice || "" });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { status: f.status, notice: f.notice || "", today_output_t: Number(f.today_output_t), capacity_tpd: Number(f.capacity_tpd), presses_active: parseInt(f.presses_active, 10), presses_total: parseInt(f.presses_total, 10) };
      await axios.put(`${API}/admin/plant`, body, { headers: authHeaders() });
      toast.success("Production updated live");
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="admin-plant-form" onSubmit={save} className="rounded-[24px] border hairline bg-[var(--forest-2)] p-6 sm:p-8 grid gap-5 sm:grid-cols-2">
      <fieldset data-testid="admin-status-options" className="sm:col-span-2">
        <legend className="text-sm font-medium mb-3">Plant status (shown on the website)</legend>
        <div role="radiogroup" className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {STATUS_OPTIONS.map(([s, label, dot]) => {
            const on = f.status === s;
            return (
              <button
                type="button"
                role="radio"
                aria-checked={on}
                key={s}
                data-testid={`admin-status-${s}`}
                onClick={() => setF({ ...f, status: s })}
                className={`flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left text-sm transition-colors ${on ? "border-[var(--husk)] bg-[#E8B04B]/10" : "hairline hover:bg-white/5"}`}
              >
                <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border ${on ? "border-[var(--husk)]" : "border-white/30"}`}>{on && <span className="h-2 w-2 rounded-full bg-[var(--husk)]" />}</span>
                <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
                {label}
              </button>
            );
          })}
        </div>
      </fieldset>
      {NUM.map(([k, label, tid]) => (
        <div key={k} className="space-y-1.5">
          <Label htmlFor={k}>{label}</Label>
          <Input id={k} data-testid={tid} type="number" step="0.1" min="0" required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-12" />
        </div>
      ))}
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="notice">Public notice (optional)</Label>
        <Input id="notice" data-testid="admin-notice-input" maxLength={200} placeholder="e.g. Second shift starts at 6 PM" value={f.notice || ""} onChange={(e) => setF({ ...f, notice: e.target.value })} className="h-12" />
      </div>
      <button data-testid="admin-plant-save" disabled={busy} className="sm:col-span-2 h-12 rounded-full bg-[var(--husk)] text-[var(--forest)] font-semibold disabled:opacity-60">Save production</button>
    </form>
  );
};
